/**
 * User-facing API error copy.
 * Builds on the BE envelope parsers in ``$lib/api/errors``.
 */
import {
  ApiErrorCode,
  describeApiError,
  isConflict,
  isExternalService,
  isForbidden,
  isInsufficientCredits,
  isNotFound,
  isOptimisticLock,
  isSubscriptionExpired,
  isUnauthorized,
  isValidationError,
  isWorkflowLocked,
} from "$lib/api/errors";
import * as m from "$lib/paraglide/messages";

export type ApiToastVariant = "info" | "success" | "warning" | "danger";

const GENERIC_MESSAGES = new Set([
  "request failed",
  "validation failed",
  "error",
  "internalerror",
  "internal error",
  "graphql response missing data",
  "failed to fetch",
  "load failed",
  "networkerror when attempting to fetch resource",
  "networkerror",
  "fetch failed",
]);

function isNetworkTransportMessage(message: string): boolean {
  const lower = message.trim().toLowerCase();
  return (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("net::err_") ||
    lower.includes("load failed") ||
    lower.includes("fetch failed")
  );
}

function isGenericMessage(message: string): boolean {
  return GENERIC_MESSAGES.has(message.trim().toLowerCase());
}

/** Localized fallback for known ApplicationError codes. */
export function apiErrorCodeMessage(code: string | null | undefined): string | null {
  switch (code) {
    case ApiErrorCode.ValidationError:
      return m.api_error_validation();
    case ApiErrorCode.NotFoundError:
      return m.api_error_not_found();
    case ApiErrorCode.AuthenticationError:
    case "UNAUTHENTICATED":
      return m.api_error_unauthorized();
    case ApiErrorCode.AuthorizationError:
      return m.api_error_forbidden();
    case ApiErrorCode.ConflictError:
      return m.api_error_conflict();
    case ApiErrorCode.ExternalServiceError:
      return m.api_error_external();
    case ApiErrorCode.InsufficientCredits:
      return m.api_error_insufficient_credits();
    case ApiErrorCode.SubscriptionExpired:
      return m.api_error_subscription_expired();
    case ApiErrorCode.OptimisticLockError:
      return m.api_error_optimistic_lock();
    case ApiErrorCode.WorkflowLocked:
      return m.api_error_workflow_locked();
    case ApiErrorCode.NoUsableTemplate:
      return m.api_error_no_template();
    case "dms_restore_entity_root_conflict":
      return m.api_error_dms_restore_entity_root();
    case "dms_restore_document_conflict":
      return m.api_error_dms_restore_document();
    case ApiErrorCode.InternalError:
      return m.api_error_internal();
    default:
      return null;
  }
}

/**
 * Prefer specific BE message / field details; fall back to localized code copy
 * for empty or generic transport messages.
 * Known product codes always use localized copy even when BE sends English text.
 */
export function userErrorMessage(err: unknown, fallback?: string): string {
  const described = describeApiError(err, fallback ?? m.api_error_generic());
  const codeFallback = apiErrorCodeMessage(described.code);

  if (
    codeFallback &&
    (described.code === "dms_restore_entity_root_conflict" ||
      described.code === "dms_restore_document_conflict" ||
      described.code === ApiErrorCode.SubscriptionExpired)
  ) {
    return codeFallback;
  }
  if (described.message && isNetworkTransportMessage(described.message)) {
    return m.api_error_network();
  }
  if (described.message && !isGenericMessage(described.message)) {
    return described.message;
  }
  if (codeFallback) return codeFallback;
  return described.message || fallback || m.api_error_generic();
}

/** Toast title + optional billing CTA description for credits / expired subscription. */
export function billingAwareToastCopy(err: unknown, fallback?: string): {
  title: string;
  description: string | null;
} {
  if (isSubscriptionExpired(err)) {
    return {
      title: m.billing_subscription_expired(),
      description: `${m.billing_subscription_expired_link()} → /settings/billing`,
    };
  }
  if (isInsufficientCredits(err)) {
    return {
      title: m.billing_insufficient(),
      description: `${m.billing_insufficient_link()} → /settings/billing`,
    };
  }
  return { title: userErrorMessage(err, fallback), description: null };
}

export function toastVariantForError(err: unknown): ApiToastVariant {
  if (
    isSubscriptionExpired(err) ||
    isInsufficientCredits(err) ||
    isConflict(err) ||
    isOptimisticLock(err) ||
    isWorkflowLocked(err)
  ) {
    return "warning";
  }
  if (isValidationError(err)) return "warning";
  if (isUnauthorized(err) || isForbidden(err) || isNotFound(err) || isExternalService(err)) {
    return "danger";
  }
  return "danger";
}
